import { FC, type JSX } from 'react';
import { border } from 'styled-system';
import styled from 'styled-components';
import breakpoint from '~/utils/style-utils';

interface BalanceProps extends React.HTMLAttributes<HTMLDivElement> {
	title?: string;
	icon?: JSX.Element;
	label?: string;
	disableLabel?: boolean;
	disableIcon?: boolean;
	iconprimary?: boolean;
	primary?: boolean;
	dataAt?: string;
	small?: boolean;
	labelFirst?: boolean;
}

interface BalanceDisplayProps extends BalanceProps {
	onSidebar?: boolean;
	borderBottomLeftRadius?: number;
	borderTopLeftRadius?: number;
	borderBottomRightRadius?: number;
	borderTopRightRadius?: number;
}

export const BalanceDisplay = styled.dl<BalanceDisplayProps>`
	align-items: center;
	background-color: ${props =>
		props.onSidebar
			? `var(--balance-chip-circle, rgba(var(--base-08), 1));`
			: `var(--shop-balance-bg, var(--balance-circle));`};
	display: inline-flex;
	padding: ${props => (props.labelFirst ? `2px` : props.small ? `2px 0 2px 8px` : `2px 0 2px 12px`)};
	margin: 0;
	border-radius: 4px;
	${border}
`;

export const BalanceText = styled.dd<{ small?: boolean }>`
	color: var(--balance-text, rgba(var(--base-02), 1));
	font: var(--caption-bold);
	margin: 0;

	${({ small }) => breakpoint.lg`
		font: ${small ? `var(--caption-bold)` : `var(--h4-bold)`};
	`}
`;

export const BalanceIconWrapper = styled.dt<{
	small?: boolean;
	disabled?: boolean;
	iconprimary?: boolean;
	primary?: boolean;
	borderRadius?: number;
}>`
	align-items: center;
	background-color: var(
		${props => (props.primary ? `--balance-icon-wrapper` : `--balance-header-icon`)},
		rgba(var(--base-09), 1)
	);
	display: flex;
	height: ${props => (props.small ? `24px` : `32px`)};
	justify-content: center;
	margin: ${props => (props.small ? `0 2px 0 4px` : `0 2px 0 12px`)};
	width: ${props => (props.small ? `24px` : `32px`)};
	filter: ${props => (props.disabled ? 'grayscale(100%)' : 'grayscale(0%)')};
	opacity: ${props => (props.disabled ? '0.2' : '1')};
	.icon--sprite {
		fill: var(${props => (props.iconprimary ? `--quick-deposit` : `--balance-fill`)}-icon-color);
		height: ${props => (props.small ? '16px' : '20px')};
		width: ${props => (props.small ? '16px' : '20px')};
	}
	border-radius: 6px;
	${border}
`;

export const BalanceTextLabelWrapper = styled.dt<{
	small?: boolean;
	labelFirst?: boolean;
	disabled?: boolean;
	borderRadius?: number;
	height?: string;
	width?: string;
}>`
	align-items: center;
	color: ${({ disabled }) => (disabled ? `rgba(var(--font-light), .5)` : `var(--quick-deposit-icon-color)`)};
	background-color: ${({ disabled }) => (disabled ? `rgba(var(--base-10), .5)` : `var(--balance-icon-wrapper)`)};
	display: flex;
	height: ${props => (props.small ? `24px` : `32px`)};
	justify-content: center;
	margin: ${props =>
		props.labelFirst ? (props.small ? `0 4px 0 0` : `0 12px 0 0`) : props.small ? `0 2px 0 4px` : `0 2px 0 12px`};

	width: auto;
	padding: 0 12px;
	font: var(--h4-bold);
	filter: ${props => (props.disabled ? 'grayscale(100%)' : 'grayscale(0%)')};
	opacity: ${props => (props.disabled ? '0.2' : '1')};
	.icon--sprite {
		fill: var(--balance-icons, rgba(var(--base-01), 1));
		height: ${props => (props.height ? props.height : '16px')};
		width: ${props => (props.width ? props.width : '16px')};
	}
	border-radius: 6px;
	${border}
`;

export const Balance: FC<BalanceProps> = ({
	title,
	icon,
	label,
	disableLabel,
	disableIcon,
	iconprimary,
	primary,
	dataAt = '',
	small,
	labelFirst,
	...rest
}) => (
	<BalanceDisplay small={small} {...rest} labelFirst={labelFirst}>
		{label && labelFirst && (
			<BalanceTextLabelWrapper disabled={disableLabel} labelFirst>
				{label}
			</BalanceTextLabelWrapper>
		)}
		<BalanceText small={small} data-at={dataAt}>
			{title}
		</BalanceText>
		{icon && (
			<BalanceIconWrapper small={small} disabled={disableIcon} iconprimary={iconprimary} primary={primary}>
				{icon}
			</BalanceIconWrapper>
		)}
		{label && !labelFirst && <BalanceTextLabelWrapper disabled={disableLabel}>{label}</BalanceTextLabelWrapper>}
	</BalanceDisplay>
);

export default Balance;
